import React, { useState } from 'react';
import { BadgeCheckIcon, ClockIcon, GraduationCapIcon, InfoIcon, MailCheckIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { LEVEL_DOES_NOT_ASSERT, LEVEL_LABEL, LEVEL_MEANING, METHOD_LABEL, STATE_LABEL, STATUS_LABEL, badgeVisible, daysUntil, expiryTone, formatDate, isSensitiveStatus } from '../../utils/verification';
import type { AcademicRecord } from '../../types/verification';

/**
 * The single verification indicator. It is deliberately not a generic tick:
 * each level renders its own words, an unverified record says so plainly rather
 * than showing nothing, and the details panel names what the badge does NOT
 * assert. Shape and text carry the meaning, never colour alone.
 */
export function AcademicBadge({
  record,
  institutionName,
  /** Whether the viewer is the owner. Owners see status; others may not. */
  owner = false,
  showDetails = true
}: {
  record: AcademicRecord;
  institutionName: string;
  owner?: boolean;
  showDetails?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const {
    verification,
    status
  } = record;
  const live = badgeVisible(verification.state);
  const alumni = status === 'alumni' || status === 'graduated';
  const label = alumni && live ? `${institutionName} alumni` : LEVEL_LABEL[verification.level];
  const days = daysUntil(verification.expiresAt);
  const tone = expiryTone(verification.expiresAt);
  const icon = !live ? <InfoIcon className="h-4 w-4" data-id="element-5635" data-mp="11s1yawky6zfm" /> : alumni ? <GraduationCapIcon className="h-4 w-4" data-id="element-5636" data-mp="11stti6amuv5d" /> : verification.level === 'email_verified' ? <MailCheckIcon className="h-4 w-4" data-id="element-5637" data-mp="16ptp1n9uwfi8" /> : <BadgeCheckIcon className="h-4 w-4" data-id="element-5638" data-mp="1bq7z1av0pziw" />;
  const badge = <Badge tone={!live ? 'neutral' : verification.level === 'email_verified' ? 'brand' : 'ok'} icon={icon} data-id="element-5639" data-mp="1brsur3t2joic">
      {live ? label : STATE_LABEL[verification.state]}
    </Badge>;
  if (!showDetails) return badge;
  return <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex min-h-[44px] items-center rounded-md" aria-label={`${live ? label : STATE_LABEL[verification.state]} — open verification details`} data-id="element-5640" data-mp="1xgzjgialqsr9">
        {badge}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Academic verification" description="What was verified, who verified it, when, and whether it is still current." data-id="element-5641" data-mp="22gvf9u95bdvq">
        <dl className="space-y-4" data-id="element-5642" data-mp="19sptd46cviub">
          <Row label="Institution" value={institutionName} data-id="element-5643" data-mp="19thralnfdsnr" />
          <Row label="Programme" value={record.programme.value} source={record.programme.source} data-id="element-5644" data-mp="1ohcg3qybbcvk" />
          {record.level && <Row label="Level" value={record.level.value} source={record.level.source} data-id="element-5645" data-mp="w0jowfckf0ap" />}
          {(owner || !isSensitiveStatus(status)) && <Row label="Status" value={STATUS_LABEL[status]} source={record.statusSource} data-id="element-5646" data-mp="r0xc270nfm2f" />}
          <Row label="Verification" value={live ? label : STATE_LABEL[verification.state]} data-id="element-5647" data-mp="32n7n6fmbvnb1" />
          <Row label="Method" value={verification.method ? METHOD_LABEL[verification.method] : '—'} data-id="element-5648" data-mp="37h3eiz836bx8" />
          <Row label="Confirmed by" value={verification.source ?? 'Nobody — this is a claim'} data-id="element-5649" data-mp="2ss7b5fgz5n2y" />
          <Row label="Last verified" value={formatDate(verification.verifiedAt)} data-id="element-5650" data-mp="2xq8rhsn0n8aq" />
          <Row label="Next verification" value={formatDate(verification.expiresAt)} data-id="element-5651" data-mp="3lgbj67n0qc8t" />
        </dl>

        <div className="mt-5 rounded-md border border-line bg-canvas p-4" data-id="element-5652" data-mp="3qdbouhfyhcze">
          <p className="text-body font-semibold text-ink" data-id="element-5653" data-mp="3cf4wfgts0574">What this means</p>
          <p className="mt-1 max-w-[68ch] text-body-sm text-ink" data-id="element-5654" data-mp="3gg6ys6izoxdb">
            {LEVEL_MEANING[verification.level]}
          </p>
          <p className="mt-2 max-w-[68ch] text-body-sm text-ink-muted" data-id="element-5655" data-mp="20yx0hy8uns4f">
            What it does not mean: {LEVEL_DOES_NOT_ASSERT[verification.level]}
          </p>
        </div>

        {live && days !== null && tone !== 'ok' && <p className="mt-3 flex items-start gap-2 text-body-sm text-ink" data-id="element-5656" data-mp="2l9r63ogo5hz7">
            <ClockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warn-700" data-id="element-5657" data-mp="2ggevii8trbpg" />
            {days > 0 ? `This verification needs renewing in ${days} days.` : 'This verification has passed its revalidation date.'}
          </p>}

        {owner && <p className="mt-3 max-w-[68ch] text-body-sm text-ink-muted" data-id="element-5658" data-mp="347akl7lwbst0">
            Your institutional identifier and any documents you submitted are held against this
            record and are never shown on your profile, in search, or to other students.
          </p>}
      </Modal>
    </>;
}
function Row({
  label,
  value,
  source
}: {
  label: string;
  value: string;
  source?: 'declared' | 'institution' | 'document' | 'integration';
}) {
  return <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-b border-line pb-3 last:border-0 last:pb-0" data-id="element-5659" data-mp="1es43dlckuobg">
      <dt className="text-body-sm text-ink-muted" data-id="element-5660" data-mp="3qg8zt9dfwjoo">{label}</dt>
      <dd className="flex flex-wrap items-center gap-2 text-body text-ink" data-id="element-5661" data-mp="3uh17vzaksijd">
        {value}
        {source && <Badge plain data-id="element-5662" data-mp="3b00q1tqe930">
            {source === 'declared' ? 'Entered by this person' : source === 'institution' ? 'From the institution' : source === 'integration' ? 'From the institutional connection' : 'From a reviewed document'}
          </Badge>}
      </dd>
    </div>;
}

/** Inline gate used wherever a policy decides whether something is available. */
export function PolicyGate({
  decision,
  feature,
  children
}: {
  decision: {
    allowed: boolean;
    message: string;
    remedy: string | null;
  };
  feature: string;
  children: React.ReactNode;
}) {
  if (decision.allowed) return <>{children}</>;
  return <div className="rounded-card border border-line bg-canvas p-4" data-id="element-5663" data-mp="2sqw5ci0onlfj">
      <div className="flex flex-wrap items-center gap-2" data-id="element-5664" data-mp="3w2ldrwfqrt3o">
        <Badge tone="neutral" icon={<InfoIcon className="h-4 w-4" data-id="element-5666" data-mp="3w4fypar0jl7r" />} data-id="element-5665" data-mp="3w34tr7lu07f2">
          Needs verification
        </Badge>
        <span className="text-body font-medium text-ink" data-id="element-5667" data-mp="3r5l6xjb2imbi">{feature}</span>
      </div>
      <p className="mt-2 max-w-[70ch] text-body-sm text-ink" data-id="element-5668" data-mp="8zgk5b9kypke">{decision.message}</p>
      {decision.remedy && <p className="mt-1.5 max-w-[70ch] text-body-sm text-ink-muted" data-id="element-5669" data-mp="40wy7auzlds1">{decision.remedy}</p>}
      <p className="mt-2 text-caption text-ink-subtle" data-id="element-5670" data-mp="ityqko6dsz7w">
        This check is also enforced by the service. Nothing here depends on the screen agreeing.
      </p>
    </div>;
}